import { apiClient } from './client'
import type { ApiResponse } from './client'
import type {
  RoleType,
  UserProfileWithRolesDto,
  UserCompanyRoleSetUpdateDto,
  UserCompanyRoleSetViewDto,
  UserProfileCreateDto,
  UserProfileCreationResultDto,
  UserRoleAssignmentDto,
  CompanyInviteStatusDto,
  ReInviteUserResultDto
} from '@/types/userRoles'
import type {
  AllCustomersAccessChangeDto as GeneratedAllCustomersAccessChangeDto,
  AllCustomersAccessDto as GeneratedAllCustomersAccessDto,
  AllCustomersAccessUpdateDto as GeneratedAllCustomersAccessUpdateDto,
  GrantManagingCompanyAccessBatchResultDto as GeneratedGrantManagingCompanyAccessBatchResultDto,
  GrantManagingCompanyAccessOutcomeDto as GeneratedGrantManagingCompanyAccessOutcomeDto,
} from '@/types/api-generated'

/**
 * Somebody in the managing company, and whether they can be given access to the company in the route.
 * IneligibleReason is finished prose from the server and is rendered as it arrives — re-deriving it
 * here would be a second, weaker copy of a rule only the server can actually enforce.
 */
export interface ManagingCompanyUserDto {
  UserProfileId: string
  Email: string
  FirstName: string
  LastName: string
  AvatarUrl: string
  CanBeAdded: boolean
  /** Null exactly when CanBeAdded is true. */
  IneligibleReason: string | null
}

/**
 * The roster, with what the screen needs to say about where these people come from.
 *
 * The managing company's name and domains travel with it rather than being fetched separately: an
 * administrator at the customer can call this but may not be able to read the managing company
 * directly, so a second call would fail for exactly the people the screen is for. The domains are
 * how a colleague's address is told apart from a stranger's — a company holds several, so comparing
 * against one canonical domain misses the address that caused the incident.
 */
export interface ManagingCompanyUsersDto {
  /** Null when this company is not managed by another; Users is then empty and no picker is offered. */
  ManagingCompanyId: string | null
  ManagingCompanyName: string | null
  ManagingCompanyDomains: string[]
  Users: ManagingCompanyUserDto[]
}

/**
 * ORB-709: granting an existing person from the managing company access to a managed company. The
 * server resolves them from the id alone, so no name or address is sent — an address on this path
 * would be a second route to the same write with nothing checking it.
 */
export interface GrantManagingCompanyAccessDto {
  UserProfileId: string
  InitialRoles: UserRoleAssignmentDto
}

/**
 * ORB-1019: several people added in one request. One result per person, in the order sent: a refusal
 * is that person's result, so the picker keeps their row ticked and Add retries just them. `Error` is
 * server prose and is shown on the row as it arrives.
 *
 * Derived from the generated DTOs, narrowing only what C# declares `string` (the role) or what nests a
 * narrowed type (the person's role set), so a server-side change to these shapes still fails the build.
 */
export interface GrantManagingCompanyAccessOutcomeDto
  extends Omit<GeneratedGrantManagingCompanyAccessOutcomeDto, 'UserProfileWithRoles'> {
  UserProfileWithRoles?: UserProfileWithRolesDto | null
}

export interface GrantManagingCompanyAccessBatchResultDto
  extends Omit<GeneratedGrantManagingCompanyAccessBatchResultDto, 'Results'> {
  Results: GrantManagingCompanyAccessOutcomeDto[]
}

/**
 * Per customer, what a change to the "Access to all customers" switch does or would do. Each list
 * holds customer names. Skipped customers are where the person is the last admin from the managing
 * company; their access is kept, as ordinary access. Restored customers are where turning it off
 * gives back the access added by hand that it replaced.
 */
export interface AllCustomersAccessChangeDto extends Omit<GeneratedAllCustomersAccessChangeDto, 'Role'> {
  Role?: RoleType | null
}

/** One person's switch. `Preview` is present only when a change was asked about. */
export interface AllCustomersAccessDto extends Omit<GeneratedAllCustomersAccessDto, 'Role' | 'Preview'> {
  Role?: RoleType | null
  Preview?: AllCustomersAccessChangeDto | null
}

export interface AllCustomersAccessUpdateDto extends Omit<GeneratedAllCustomersAccessUpdateDto, 'Role'> {
  Role?: RoleType | null
}

// User roles API functions
export const userRolesApi = {
  /**
   * Get all user role assignments for a specific company
   */
  getUserRolesByCompany: (clientCompanyId: string): Promise<ApiResponse<UserProfileWithRolesDto[]>> =>
    apiClient.get(`/api/UserCompanyRoles/Company/${clientCompanyId}`),

  /** One person's role set at one company. Re-read after a change the caller did not make itself. */
  getUserCompanyRoleSet: (
    userProfileId: string,
    clientCompanyId: string
  ): Promise<ApiResponse<UserCompanyRoleSetViewDto>> =>
    apiClient.get(`/api/UserCompanyRoles/${userProfileId}/${clientCompanyId}`),

  /**
   * Update user company role set. The route carries the tenant
   * (userProfileId + clientCompanyId); the server forces Id from the route (ORB-306).
   */
  updateUserCompanyRoleSet: (
    userProfileId: string,
    clientCompanyId: string,
    updateDto: UserCompanyRoleSetUpdateDto
  ): Promise<ApiResponse<UserCompanyRoleSetViewDto>> =>
    apiClient.put(`/api/UserCompanyRoles/${userProfileId}/${clientCompanyId}`, updateDto),

  /**
   * Create a new user with initial role assignments
   */
  createUser: (
    clientCompanyId: string,
    createDto: UserProfileCreateDto
  ): Promise<ApiResponse<UserProfileCreationResultDto>> =>
    apiClient.post(`/api/Users/ClientCompany/${clientCompanyId}/CreateUser`, createDto),

  /**
   * The managing company's people, for giving one of them access to this company. Comes back empty
   * when this company has no managing company, so the caller never has to ask that first.
   */
  getManagingCompanyUsers: (
    clientCompanyId: string,
    searchTerm?: string
  ): Promise<ApiResponse<ManagingCompanyUsersDto>> =>
    apiClient.get(
      `/api/ClientCompanies/${clientCompanyId}/ManagingCompanyUsers` +
      (searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '')
    ),

  /**
   * Gives one of the managing company's people access to this company, naming them by profile id.
   * Separate from createUser because nothing about them is supplied by the caller — no name, no
   * address, so no domain to check and nothing to mistype.
   */
  grantManagingCompanyAccess: (
    clientCompanyId: string,
    grantDto: GrantManagingCompanyAccessDto
  ): Promise<ApiResponse<UserProfileCreationResultDto>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/ManagingCompanyUsers`, grantDto),

  /**
   * ORB-1019: gives several of the managing company's people access at once, each with their own roles.
   * Answers 200 with a result per person even when some were refused.
   */
  grantManagingCompanyAccessBatch: (
    clientCompanyId: string,
    grants: GrantManagingCompanyAccessDto[]
  ): Promise<ApiResponse<GrantManagingCompanyAccessBatchResultDto>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/ManagingCompanyUsers/Batch`, { Grants: grants }),

  /**
   * ORB-1019: one person's "Access to all customers" switch. The route names the managing company, so
   * only its admins get an answer. Passing `proposed` also previews what saving it would do.
   */
  getAllCustomersAccess: (
    managingCompanyId: string,
    userProfileId: string,
    proposed?: AllCustomersAccessUpdateDto
  ): Promise<ApiResponse<AllCustomersAccessDto>> => {
    const query = proposed
      ? `?enabled=${proposed.Enabled}` + (proposed.Enabled && proposed.Role ? `&role=${encodeURIComponent(proposed.Role)}` : '')
      : ''
    return apiClient.get(`/api/ClientCompanies/${managingCompanyId}/Users/${userProfileId}/AllCustomersAccess${query}`)
  },

  setAllCustomersAccess: (
    managingCompanyId: string,
    userProfileId: string,
    update: AllCustomersAccessUpdateDto
  ): Promise<ApiResponse<AllCustomersAccessChangeDto>> =>
    apiClient.put(`/api/ClientCompanies/${managingCompanyId}/Users/${userProfileId}/AllCustomersAccess`, update),

  /**
   * Soft delete a user from a client company
   */
  deleteUser: (
    userProfileId: string,
    clientCompanyId: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/Users/${userProfileId}/ClientCompany/${clientCompanyId}`),

  /**
   * Per-person invite status. Deliberately a second call rather than part of the user list: the list must
   * never depend on Auth0, or an Auth0 incident takes away the ability to manage users — including the
   * ability to re-invite.
   */
  getInviteStatus: (
    clientCompanyId: string
  ): Promise<ApiResponse<CompanyInviteStatusDto>> =>
    apiClient.get(`/api/UserCompanyRoles/Company/${clientCompanyId}/InviteStatus`),

  /** Replaces a pending invitation with a fresh one, revoking the old link. */
  reInviteUser: (
    userProfileId: string,
    clientCompanyId: string
  ): Promise<ApiResponse<ReInviteUserResultDto>> =>
    apiClient.post(`/api/Users/${userProfileId}/ClientCompany/${clientCompanyId}/ReInvite`, {}),
}
